import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { SignOut, CheckCircle, Ear, Waveform, Footprints, Person, UsersThree, Confetti, BookOpen, Printer } from "@phosphor-icons/react";
import { rcApi, RC, errMsg } from "./rcApi";
import { RcCard, RcTitle, RcButton, VideoCard, MusicBlock, RulesBox, RcFooter, Tag, RetourRencontre } from "./rcUi";
import { Field } from "./RythmesConnexionsPublic";
import { Rencontres, meetingLabel } from "./Rencontres";

const STEP_ICONS = [Ear, Waveform, Footprints, Person, UsersThree, Confetti];

export default function RythmesParcours() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    if (!localStorage.getItem("rc_token")) { setLoading(false); return; }
    rcApi.get("/parcours").then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    document.title = "Mes premiers pas — Rythmes & Connexions";
    const email = params.get("email"), token = params.get("token");
    if (email && token) {
      rcApi.post("/auth/verify", { email, token }).then((r) => {
        localStorage.setItem("rc_token", r.data.token);
        nav("/rythmes/mon-parcours", { replace: true });
        load();
      }).catch((e) => { toast.error(errMsg(e)); setLoading(false); });
    } else load();
  }, [params, nav, load]);

  const logout = () => { localStorage.removeItem("rc_token"); setData(null); };

  if (loading) return <Shell><p className="text-center py-20" style={{ color: RC.muted }}>Chargement…</p></Shell>;
  if (!data) return <Shell><Login defaultEmail={params.get("email") || ""} onDone={load} /></Shell>;
  return <Shell member={data.member} onLogout={logout}><Parcours data={data} setData={setData} /></Shell>;
}

function Shell({ children, member, onLogout }) {
  return (
    <div className="min-h-screen font-manrope" style={{ background: RC.bg, color: RC.cream }} data-testid="rc-parcours-page">
      <header className="sticky top-0 z-30 backdrop-blur-md" style={{ background: RC.bg + "e6", borderBottom: `1px solid ${RC.line}` }}>
        <div className="max-w-3xl mx-auto px-5 h-16 flex items-center justify-between gap-3">
          <Link to="/rythmes-connexions" className="font-cabinet font-bold text-lg leading-none">Rythmes <span style={{ color: RC.coral }}>&</span> Connexions</Link>
          {member && (
            <div className="flex items-center gap-3 text-sm">
              <span data-testid="rc-member-name" className="hidden sm:inline" style={{ color: RC.muted }}>Bonjour {member.prenom}</span>
              <button onClick={onLogout} data-testid="rc-logout" className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 hover:opacity-80" style={{ borderColor: RC.line }}><SignOut size={16} /> Quitter</button>
            </div>
          )}
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 py-8 sm:py-12">{children}</main>
      <RcFooter />
    </div>
  );
}

function Login({ defaultEmail, onDone }) {
  const [email, setEmail] = useState(defaultEmail);
  const [code, setCode] = useState("");
  const [step, setStep] = useState(defaultEmail ? "code" : "email");
  const [busy, setBusy] = useState(false);
  const [debug, setDebug] = useState(null);
  const request = async (e) => {
    e?.preventDefault(); setBusy(true);
    try {
      const r = await rcApi.post("/auth/request", { email, origin: window.location.origin });
      if (!r.data.ok) { toast.error(r.data.message); return; }
      toast.success(r.data.message); setDebug(r.data.debug_code || null); setStep("code");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const verify = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await rcApi.post("/auth/verify", { email, code });
      localStorage.setItem("rc_token", r.data.token); onDone();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-md mx-auto">
      <RcTitle kicker="Mon parcours">Entrer dans mon espace</RcTitle>
      <RcCard>
        {step === "email" ? (
          <form onSubmit={request} className="grid gap-4" data-testid="rc-login-form">
            <Field label="Mon courriel" type="email" value={email} onChange={setEmail} required testid="rc-login-email" />
            <RcButton type="submit" disabled={busy} data-testid="rc-login-request">{busy ? "Envoi…" : "Recevoir mon lien"}</RcButton>
            <p className="text-xs" style={{ color: RC.muted }}>Pas encore inscrit ? <Link to="/rythmes-connexions#acces" className="underline" style={{ color: RC.gold }}>Recevoir mon accès</Link></p>
          </form>
        ) : (
          <form onSubmit={verify} className="grid gap-4" data-testid="rc-code-form">
            <p className="text-sm" style={{ color: RC.muted }}>Clique sur le lien reçu par courriel ({email}) ou entre le code à 6 chiffres :</p>
            {debug && <div className="rounded-xl p-3 text-center font-mono text-2xl tracking-[0.4em]" style={{ background: RC.bg }} data-testid="rc-login-debug-code">{debug}</div>}
            <Field label="Code à 6 chiffres" value={code} onChange={setCode} required testid="rc-login-code" />
            <RcButton type="submit" disabled={busy} data-testid="rc-login-verify">{busy ? "Vérification…" : "Entrer"}</RcButton>
            <button type="button" onClick={request} className="text-sm underline" style={{ color: RC.muted }} data-testid="rc-login-resend">Renvoyer un lien</button>
            <button type="button" onClick={() => setStep("email")} className="text-sm underline" style={{ color: RC.muted }}>Changer de courriel</button>
          </form>
        )}
      </RcCard>
    </div>
  );
}

function Parcours({ data, setData }) {
  const { settings, sessions, resources, tracks, playlists = [], skills, levels, retour_questions, progress, member, prochaine } = data;
  const current = sessions.find((s) => s.numero === settings.module_courant) || sessions[0];
  const [selected, setSelected] = useState(current?.id);
  const mod = sessions.find((s) => s.id === selected) || current;
  const byId = (list) => (ids = []) => ids.map((id) => list.find((x) => x.id === id)).filter(Boolean);
  const playlist = playlists.find((p) => p.id === mod?.playlist_id);
  const modRes = byId(resources)(mod?.resources), modTracks = byId(tracks)(playlist ? playlist.track_ids : mod?.tracks);
  const musicUrl = playlist?.spotify_url || modTracks.find((t) => t.spotify_url)?.spotify_url || settings.playlist_url;

  return (
    <div className="space-y-12">
      <section>
        <div className="text-[11px] uppercase tracking-[0.35em] mb-2" style={{ color: RC.gold }}>Bienvenue {member.prenom}</div>
        <h1 className="text-3xl sm:text-4xl font-bold font-cabinet">Mes premiers pas</h1>
        <p className="mt-3 leading-relaxed" style={{ color: RC.muted }}>{settings.intro}</p>
        <RcCard className="mt-5 !py-4" data-testid="rc-next-meeting">
          <span className="text-sm" style={{ color: RC.muted }}>Ma prochaine rencontre · </span>
          <span className="font-semibold capitalize">{meetingLabel(prochaine, settings)}</span>
          <span style={{ color: RC.muted }}> — {prochaine?.lieu || settings.lieu}</span>
        </RcCard>
      </section>

      {sessions.length > 1 && (
        <div className="flex gap-2 flex-wrap" data-testid="rc-module-tabs">
          {sessions.map((s) => (
            <button key={s.id} onClick={() => setSelected(s.id)} data-testid={`rc-module-tab-${s.numero}`} className="rounded-full px-4 py-2 text-sm font-semibold border"
              style={{ borderColor: s.id === mod.id ? RC.coral : RC.line, color: s.id === mod.id ? RC.coral : RC.muted }}>Rencontre {s.numero}</button>
          ))}
        </div>
      )}

      {mod && (
        <section data-testid="rc-current-module">
          <RcTitle kicker={`Le module de la semaine · Rencontre ${mod.numero}`}>{mod.theme}</RcTitle>
          {mod.objectif && <p className="mb-5 text-base" style={{ color: RC.turquoise }}>Objectif : {mod.objectif}</p>}
          <ol className="space-y-3">
            {(mod.etapes || []).map((e, i) => {
              const Icon = STEP_ICONS[i] || Confetti;
              return (
                <li key={i} className="flex gap-4 rounded-2xl border p-4" style={{ background: RC.card, borderColor: RC.line }} data-testid={`rc-step-${i + 1}`}>
                  <span className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ background: RC.turquoise + "22" }}><Icon size={22} color={RC.turquoise} /></span>
                  <div><div className="font-bold"><span style={{ color: RC.gold }}>Étape {i + 1}</span> — {e.titre}</div><p className="mt-1 leading-relaxed">{e.consigne}</p></div>
                </li>
              );
            })}
          </ol>
          {mod.ecoutons && <RcCard className="mt-4"><div className="text-[11px] uppercase tracking-widest mb-1" style={{ color: RC.gold }}>Ce qu'on écoute</div><p>{mod.ecoutons}</p></RcCard>}
          {mod.exercice && <RcCard className="mt-3"><div className="text-[11px] uppercase tracking-widest mb-1" style={{ color: RC.gold }}>Exercice de 5 minutes</div><p>{mod.exercice}</p></RcCard>}
          <Link to={`/rythmes/fiche/${mod.numero}`} data-testid="rc-fiche-link" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold hover:underline" style={{ color: RC.gold }}><Printer size={18} /> Fiche imprimable de cette rencontre</Link>
        </section>
      )}

      {mod?.retour_rencontre && <RetourRencontre retour={mod.retour_rencontre} />}

      <section>
        <RcTitle kicker="Vidéos et ressources">Pour revoir à la maison</RcTitle>
        {modRes.length ? <div className="grid gap-5">{modRes.map((r) => <VideoCard key={r.id} r={r} />)}</div>
          : <p style={{ color: RC.muted }}>Les ressources de cette rencontre seront ajoutées prochainement.</p>}
      </section>

      <section><RcTitle kicker="Écoute et pratique personnelle">Musique pour pratiquer</RcTitle><MusicBlock url={musicUrl} note={settings.playlist_note} tracks={modTracks} title={playlist?.nom} /></section>

      <Rencontres />
      <Carnet skills={skills} levels={levels} progress={progress} onSaved={(p) => setData({ ...data, progress: p })} />
      <Retour mod={mod} questions={retour_questions} progress={progress} onSaved={(p) => setData({ ...data, progress: p })} />
      <Library resources={resources} rubriques={data.rubriques} />
      <RulesBox regles={settings.regles} />
    </div>
  );
}

function Carnet({ skills, levels, progress, onSaved }) {
  const [vals, setVals] = useState(progress.skills || {});
  const [busy, setBusy] = useState(false);
  const set = async (k, v) => {
    const next = { ...vals, [k]: vals[k] === v ? 0 : v }; setVals(next); setBusy(true);
    try { const r = await rcApi.put("/progress", { skills: next }); onSaved(r.data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <section data-testid="rc-carnet">
      <RcTitle kicker="Mon carnet de progression">Où j'en suis, à mon rythme</RcTitle>
      <p className="text-sm mb-4" style={{ color: RC.muted }}>Visible par toi et l'encadrant seulement. Aucun classement, aucune comparaison.</p>
      <div className="space-y-3">
        {skills.map((s) => (
          <RcCard key={s.key} className="!p-4" data-testid={`rc-skill-${s.key}`}>
            <div className="font-semibold mb-3">{s.label}</div>
            <div className="grid grid-cols-3 gap-2">
              {[1, 2, 3].map((lv) => {
                const on = vals[s.key] === lv;
                return (
                  <button key={lv} disabled={busy} onClick={() => set(s.key, lv)} data-testid={`rc-skill-${s.key}-${lv}`}
                    className="rounded-xl px-2 py-2.5 text-sm font-semibold border transition-colors flex items-center justify-center gap-1.5"
                    style={{ borderColor: on ? RC.turquoise : RC.line, background: on ? RC.turquoise + "22" : "transparent", color: on ? RC.turquoise : RC.muted }}>
                    {on && <CheckCircle size={16} weight="fill" />}{levels[lv]}
                  </button>
                );
              })}
            </div>
          </RcCard>
        ))}
      </div>
    </section>
  );
}

function Retour({ mod, questions, progress, onSaved }) {
  const key = String(mod?.numero || 1);
  const [ans, setAns] = useState((progress.retours || {})[key] || {});
  const [busy, setBusy] = useState(false);
  useEffect(() => { setAns((progress.retours || {})[key] || {}); }, [key, progress.retours]);
  const save = async () => {
    setBusy(true);
    try { const r = await rcApi.put("/progress", { retours: { ...(progress.retours || {}), [key]: ans } }); onSaved(r.data); toast.success("Retour enregistré — merci !"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <section data-testid="rc-retour">
      <RcTitle kicker={`Mon retour sur la rencontre ${key}`}>Trois questions, facultatives</RcTitle>
      <RcCard className="grid gap-4">
        {questions.map((q) => <Field key={q.key} label={q.label} value={ans[q.key] || ""} onChange={(v) => setAns({ ...ans, [q.key]: v })} textarea testid={`rc-retour-${q.key}`} />)}
        <RcButton variant="gold" onClick={save} disabled={busy} data-testid="rc-retour-save">{busy ? "Enregistrement…" : "Enregistrer mon retour"}</RcButton>
      </RcCard>
    </section>
  );
}

function Library({ resources, rubriques }) {
  const [open, setOpen] = useState(false);
  const [rub, setRub] = useState(rubriques[0]?.key);
  const list = resources.filter((r) => r.rubrique === rub);
  return (
    <section data-testid="rc-library">
      <RcTitle kicker="Bibliothèque">Toutes les ressources</RcTitle>
      {!open ? <RcButton variant="ghost" onClick={() => setOpen(true)} data-testid="rc-library-open"><BookOpen size={20} /> Explorer la bibliothèque</RcButton> : (
        <>
          <div className="flex gap-2 flex-wrap mb-4">
            {rubriques.map((r) => <button key={r.key} onClick={() => setRub(r.key)} data-testid={`rc-rub-${r.key}`} className="rounded-full px-3.5 py-1.5 text-sm border"
              style={{ borderColor: rub === r.key ? RC.gold : RC.line, color: rub === r.key ? RC.gold : RC.muted }}>{r.titre}</button>)}
          </div>
          {list.length ? <div className="grid gap-4">{list.map((r) => <VideoCard key={r.id} r={r} compact />)}</div>
            : <p style={{ color: RC.muted }}>Rubrique en préparation — <Tag>à venir</Tag></p>}
        </>
      )}
    </section>
  );
}
